import { memo, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { CurrencyProvider } from 'contexts/currencyContext'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { parseFlightSearchParams } from './Flights/parseFlightSearchParams'
import { selectFlightFareFamilies } from './Flights/selectFlightFareFamilies'
import DrawerFlightCard from './Flights/DrawerFlightCard'
import { buildFlightContext } from './Flights/buildFlightContext'
import { buildMultiCityFlightMessage } from './Flights/multiCityFlightMessage'
import { getFlightOptionJourneyKey, isMultiCityFlightRecommendationItem, optionToJourney } from './chatFlightTypes'
import type { FlightRecommendationItem, FlightRecommendationMultiCityItem, FlightRecommendationsUIData, FlightRecommendationSingleItem, FlightOption } from './chatFlightTypes'

interface Props {
  data: FlightRecommendationsUIData
  onSendMessage?: (message: string) => void
}

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
`

const RecommendationShell = styled.div<{ $isUnavailable: boolean }>`
  opacity: ${props => props.$isUnavailable ? 0.58 : 1};
`

const ReturnRecommendationShell = styled(Sheet)<{ $isUnavailable: boolean }>`
  opacity: ${props => props.$isUnavailable ? 0.58 : 1};
  padding: ${rem(12)};
`

const PairGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const PairFooter = styled.div`
  align-items: center;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  display: flex;
  gap: ${rem(12)};
  justify-content: space-between;
  padding-top: ${rem(12)};
`

function getJourneyKey(option: FlightOption): string | undefined {
  return getFlightOptionJourneyKey(option)
}

function getRecommendationKey(item: FlightRecommendationItem, index: number): string {
  if (isMultiCityFlightRecommendationItem(item)) {
    const refs = item.flights.map(flight => getJourneyKey(flight) ?? flight.leg.flightNumber).join('-')
    return `${refs}-${index}`
  }

  const outboundKey = getJourneyKey(item.outbound) ??
    `${item.outbound.leg.flightNumber}-${item.outbound.leg.departureDate}-${item.outbound.leg.departureTime}-${item.outbound.leg.arrivalAirport}`
  const inboundKey = item.inbound ?
    getJourneyKey(item.inbound) ??
    `${item.inbound.leg.flightNumber}-${item.inbound.leg.departureDate}-${item.inbound.leg.departureTime}-${item.inbound.leg.arrivalAirport}` :
    'one-way'

  return `${outboundKey}-${inboundKey}-${index}`
}

function ChatFlightMultiCityRecommendationCard({
  item,
  index,
  onSendMessage,
}: {
  item: FlightRecommendationMultiCityItem
  index: number
  onSendMessage?: (message: string) => void
}) {
  const intl = useIntl()

  const journeys = useMemo(
    () => item.flights.map((flight, flightIndex) =>
      optionToJourney(flight, `flight-recommendation-${index}-multi-city-${flightIndex}`, flight.currencyCode ?? item.currencyCode),
    ),
    [index, item.currencyCode, item.flights],
  )

  const selectLabel = intl.formatMessage({
    id: 'FOREyFIFyoG8zaEUykey',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a flight to the trip planner itinerary from the drawer search results',
  })

  const handleSelect = useCallback(() => {
    if (!onSendMessage || journeys.length === 0) return

    const firstJourney = journeys[0]
    const firstFlights = firstJourney.flightGroup.flights
    const lastJourney = journeys[journeys.length - 1]
    const lastFlights = lastJourney.flightGroup.flights
    const from = firstFlights[0].departureAirport
    const to = lastFlights[lastFlights.length - 1].arrivalAirport
    const { userMessage, contextContent } = buildMultiCityFlightMessage({
      intl,
      from,
      to,
      selectedJourneyList: journeys,
      groupCurrency: item.currencyCode,
      combinedTotal: item.totalPrice,
      buildFlightContext,
    })
    const searchMeta = [
      item.searchId ? `Search ID: ${item.searchId}` : undefined,
      item.searchExpiry ? `Search expiry: ${item.searchExpiry}` : undefined,
      item.searchParams ? `Search params: ${item.searchParams}` : undefined,
    ].filter((line): line is string => !!line)

    const metadataContext = searchMeta.length > 0 ? `\n\n${searchMeta.join('\n')}` : ''
    onSendMessage(`${userMessage}\n<Context label="Flight search result">${contextContent}${metadataContext}</Context>`)
  }, [intl, item.currencyCode, item.searchExpiry, item.searchId, item.searchParams, item.totalPrice, journeys, onSendMessage])

  const recommendationMemberPrice = item.pricePerPerson
  const recommendationRegularPrice = item.regularPricePerPerson
  const showRecommendationLuxPlusPrice = item.isLuxPlusFare === true && Number.isFinite(recommendationRegularPrice)

  return (
    <ReturnRecommendationShell border="neutral-five" borderRadius="L" $isUnavailable={false}>
      <PairGrid>
        {journeys.map((journey, journeyIndex) => (
          <Group key={journey.id} direction="vertical" gap={8}>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'xmce4KMu4_6CYz9hwEpo',
                  defaultMessage: 'Flight {flightNumber}',
                  description: 'Label for a multi-city flight segment with its sequence number',
                },
                { flightNumber: journeyIndex + 1 },
              )}
            </BodyText>
            <DrawerFlightCard
              journey={journey}
              showSelectButton={false}
              showPrice={false}
              alignColumns
              responsiveCompact
            />
          </Group>
        ))}
        <PairFooter>
          <CurrencyProvider currency={item.currencyCode}>
            {showRecommendationLuxPlusPrice ?
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={recommendationMemberPrice}
                  nonMemberPrice={recommendationRegularPrice}
                  saleUnit="adult"
                /> :
                <PriceRowPrice size="S" price={recommendationMemberPrice} saleUnit="adult" />}
          </CurrencyProvider>
          {onSendMessage && (
            <TextButton kind="secondary" size="small" onClick={handleSelect}>
              {selectLabel}
            </TextButton>
          )}
        </PairFooter>
      </PairGrid>
    </ReturnRecommendationShell>
  )
}

function ChatFlightRecommendationCard({
  item,
  index,
  onSendMessage,
}: {
  item: FlightRecommendationSingleItem
  index: number
  onSendMessage?: (message: string) => void
}) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const outboundJourney = useMemo(
    () => optionToJourney(item.outbound, `flight-recommendation-${index}-outbound`, item.currencyCode),
    [index, item.currencyCode, item.outbound],
  )

  const inboundJourney = useMemo(
    () => item.inbound
      ? optionToJourney(item.inbound, `flight-recommendation-${index}-inbound`, item.currencyCode)
      : undefined,
    [index, item.currencyCode, item.inbound],
  )

  const handleOutboundDetails = useCallback(() => {
    showModal(<JourneyV2DetailsModal journey={outboundJourney} />)
  }, [outboundJourney, showModal])

  const handleInboundDetails = useCallback(() => {
    if (!inboundJourney) return
    showModal(<JourneyV2DetailsModal journey={inboundJourney} />)
  }, [inboundJourney, showModal])

  const recommendationMemberPrice = item.pricePerPerson
  const recommendationRegularPrice = item.regularPricePerPerson
  const showRecommendationLuxPlusPrice = item.isLuxPlusFare === true && Number.isFinite(recommendationRegularPrice)

  if (onSendMessage) {
    return (
      <SelectableFlightRecommendationCard
        item={item}
        outboundJourney={outboundJourney}
        inboundJourney={inboundJourney}
        handleOutboundDetails={handleOutboundDetails}
        handleInboundDetails={handleInboundDetails}
        onSendMessage={onSendMessage}
      />
    )
  }

  if (!inboundJourney) {
    return (
      <RecommendationShell $isUnavailable={false}>
        <Group direction="vertical" gap={8}>
          <DrawerFlightCard
            journey={outboundJourney}
            onViewDetails={handleOutboundDetails}
            showSelectButton={false}
            alignColumns
            responsiveCompact
            compactVariant="recommendation"
          />
        </Group>
      </RecommendationShell>
    )
  }

  return (
    <ReturnRecommendationShell border="neutral-five" borderRadius="L" $isUnavailable={false}>
      <PairGrid>
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'IQ3f1jGdw73Qp38g_sUn',
            defaultMessage: 'Departing flight',
            description: 'Label for the outbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={outboundJourney}
          onViewDetails={handleOutboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'm0vOQQ2JazCwANddE4sc',
            defaultMessage: 'Returning flight',
            description: 'Label for the inbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={inboundJourney}
          onViewDetails={handleInboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <PairFooter>
          <CurrencyProvider currency={item.currencyCode}>
            {showRecommendationLuxPlusPrice ?
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={recommendationMemberPrice}
                  nonMemberPrice={recommendationRegularPrice}
                  saleUnit="adult"
                /> :
                <PriceRowPrice size="S" price={recommendationMemberPrice} saleUnit="adult" />}
          </CurrencyProvider>
        </PairFooter>
      </PairGrid>
    </ReturnRecommendationShell>
  )
}

interface SelectableFlightRecommendationCardProps {
  item: FlightRecommendationSingleItem
  outboundJourney: App.JourneyV2
  inboundJourney: App.JourneyV2 | undefined
  handleOutboundDetails: () => void
  handleInboundDetails: () => void
  onSendMessage: (message: string) => void
}

function SelectableFlightRecommendationCard({
  item,
  outboundJourney,
  inboundJourney,
  handleOutboundDetails,
  handleInboundDetails,
  onSendMessage,
}: SelectableFlightRecommendationCardProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const occupants = useMemo<App.Occupants>(
    () => parseFlightSearchParams(item.searchParams)?.occupancies[0] ?? { adults: item.passengers ?? 1 },
    [item.searchParams, item.passengers],
  )

  const selectLabel = intl.formatMessage({
    id: 'FOREyFIFyoG8zaEUykey',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a flight to the trip planner itinerary from the drawer search results',
  })
  const recommendationMemberPrice = item.pricePerPerson
  const recommendationRegularPrice = item.regularPricePerPerson
  const showRecommendationLuxPlusPrice = item.isLuxPlusFare === true && Number.isFinite(recommendationRegularPrice)

  const handleSelect = useCallback(async() => {
    const outFlights = outboundJourney.flightGroup.flights
    const from = outFlights[0].departureAirport
    const finalJourney = inboundJourney ?? outboundJourney
    const finalFlights = finalJourney.flightGroup.flights
    const to = finalFlights[finalFlights.length - 1].arrivalAirport

    // Ask for the fare before the message goes out, so the assistant receives
    // the flight and the chosen fare together and the traveller is never sent
    // back to the cart to answer the same question a second time.
    const fareResult = await selectFlightFareFamilies({
      departingJourney: outboundJourney,
      returningJourney: inboundJourney,
      occupants,
      searchId: item.searchId,
      sessionId: item.sessionId,
      showModal,
      journeysFromSearch: false,
    })
    if (fareResult.status !== 'selected') return
    const { departingFareFamily, returningFareFamily } = fareResult.selection

    let userMessage: string
    let contextContent: string

    if (inboundJourney) {
      userMessage = intl.formatMessage(
        { id: 'ZE1Bw2jDpdHXdHmw312L', defaultMessage: 'Add return flight from {from} to {to}', description: 'User chat message shown when adding a return flight from the flight search drawer' },
        { from, to },
      )
      contextContent = `Departing flight:\n${buildFlightContext(outboundJourney, departingFareFamily)}\n\nReturning flight:\n${buildFlightContext(inboundJourney, returningFareFamily)}`
    } else {
      userMessage = intl.formatMessage(
        { id: 'mtc-4uBZ7D3tkBZV8ueR', defaultMessage: 'Add flight from {from} to {to}', description: 'User chat message shown when adding a one-way flight from the flight search drawer' },
        { from, to },
      )
      contextContent = buildFlightContext(outboundJourney, departingFareFamily)
    }

    const searchMeta = [
      item.searchId ? `Search ID: ${item.searchId}` : undefined,
      item.searchExpiry ? `Search expiry: ${item.searchExpiry}` : undefined,
      item.searchParams ? `Search params: ${item.searchParams}` : undefined,
    ].filter((line): line is string => !!line)
    const metadataContext = searchMeta.length > 0 ? `\n\n${searchMeta.join('\n')}` : ''

    onSendMessage(`${userMessage}\n<Context label="Flight search result">${contextContent}${metadataContext}</Context>`)
  }, [inboundJourney, intl, item.searchExpiry, item.searchId, item.searchParams, item.sessionId, occupants, onSendMessage, outboundJourney, showModal])

  if (!inboundJourney) {
    return (
      <RecommendationShell $isUnavailable={false}>
        <Group direction="vertical" gap={8}>
          <DrawerFlightCard
            journey={outboundJourney}
            onSelect={handleSelect}
            onViewDetails={handleOutboundDetails}
            selectLabel={selectLabel}
            alignColumns
            responsiveCompact
            compactVariant="recommendation"
          />
        </Group>
      </RecommendationShell>
    )
  }

  return (
    <ReturnRecommendationShell border="neutral-five" borderRadius="L" $isUnavailable={false}>
      <PairGrid>
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'IQ3f1jGdw73Qp38g_sUn',
            defaultMessage: 'Departing flight',
            description: 'Label for the outbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={outboundJourney}
          onViewDetails={handleOutboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'm0vOQQ2JazCwANddE4sc',
            defaultMessage: 'Returning flight',
            description: 'Label for the inbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={inboundJourney}
          onViewDetails={handleInboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <PairFooter>
          <CurrencyProvider currency={item.currencyCode}>
            {showRecommendationLuxPlusPrice ?
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={recommendationMemberPrice}
                  nonMemberPrice={recommendationRegularPrice}
                  saleUnit="adult"
                /> :
                <PriceRowPrice size="S" price={recommendationMemberPrice} saleUnit="adult" />}
          </CurrencyProvider>
          <TextButton kind="secondary" size="small" onClick={handleSelect}>
            {selectLabel}
          </TextButton>
        </PairFooter>
      </PairGrid>
    </ReturnRecommendationShell>
  )
}

function ChatFlightRecommendations({ data, onSendMessage }: Props) {
  if (data.items.length === 0) {
    return null
  }

  return (
    <RecommendationsList>
      {data.items.map((item, index) => (
        isMultiCityFlightRecommendationItem(item) ? (
          <ChatFlightMultiCityRecommendationCard
            key={getRecommendationKey(item, index)}
            item={item}
            index={index}
            onSendMessage={onSendMessage}
          />
        ) : (
          <ChatFlightRecommendationCard
            key={getRecommendationKey(item, index)}
            item={item}
            index={index}
            onSendMessage={onSendMessage}
          />
        )
      ))}
    </RecommendationsList>
  )
}

export default memo(ChatFlightRecommendations)
